import { useNavigate, useSearchParams } from 'react-router-dom'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { Screen } from '../components/Screen'
import { EmptyState, ErrorState, Skeleton } from '../components/States'
import { useEnv } from '../context'
import { EVENT_KIND_LABEL, eventsOnDay, formatEventWhen, isOnDay, splitEvents } from '../data/events'
import { useEvents, useSessions } from '../data/hooks'
import {
  addDays,
  formatDayLong,
  formatMonth,
  formatTimeRange,
  formatWeekRange,
  isoDate,
  parseIsoDate,
  startOfWeek,
  weekdayShort,
} from '../data/schedule'
import type { ClubEvent, SessionSlot } from '../data/types'

type Mode = 'day' | 'week' | 'month'
const MODES: { id: Mode; label: string }[] = [
  { id: 'day', label: 'День' },
  { id: 'week', label: 'Неделя' },
  { id: 'month', label: 'Месяц' },
]
const WEEK_HEAD = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс']

function visibleRange(mode: Mode, selected: Date): [Date, Date] {
  if (mode === 'day') return [selected, selected]
  if (mode === 'week') {
    const monday = startOfWeek(selected)
    return [monday, addDays(monday, 6)]
  }
  const first = new Date(selected.getFullYear(), selected.getMonth(), 1)
  const gridStart = startOfWeek(first)
  return [gridStart, addDays(gridStart, 41)]
}

/**
 * S04 «Расписание». Дата и режим лежат в адресе (?d=&m=), поэтому возврат из
 * занятия приходит на тот же день и режим (контракт S04).
 */
export function ScheduleScreen() {
  const { now, toast } = useEnv()
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const today = now()
  const mode: Mode = (['day', 'week', 'month'] as const).find((m) => m === params.get('m')) ?? 'week'
  const selected = parseIsoDate(params.get('d')) ?? today

  const [from, to] = visibleRange(mode, selected)
  const sessions = useSessions(isoDate(from), isoDate(to))

  const update = (next: { m?: Mode; d?: Date }) => {
    const p = new URLSearchParams(params)
    p.set('m', next.m ?? mode)
    p.set('d', isoDate(next.d ?? selected))
    // replace: перелистывание дней не должно забивать историю «назад».
    setParams(p, { replace: true })
  }

  const step = (dir: -1 | 1) => {
    if (mode === 'day') update({ d: addDays(selected, dir) })
    else if (mode === 'week') update({ d: addDays(selected, dir * 7) })
    else update({ d: new Date(selected.getFullYear(), selected.getMonth() + dir, 1) })
  }

  const daysWithSessions = new Set((sessions.data ?? []).filter((s) => !s.cancelled).map((s) => s.date))
  const dayList = (sessions.data ?? []).filter((s) => s.date === isoDate(selected))
  // События (соревнования, сборы) - на днях календаря и карточкой дня (events.md)
  const events = useEvents()
  const allEvents = events.data ?? []
  const hasEvent = (iso: string) => allEvents.some((e) => isOnDay(e, iso))
  const dayEvents = eventsOnDay(allEvents, isoDate(selected))
  const nextEvent = splitEvents(allEvents, isoDate(today)).upcoming[0]
  const dayLabel = (d: Date, iso: string) =>
    `${formatDayLong(d)}${daysWithSessions.has(iso) ? ', есть занятия' : ''}${hasEvent(iso) ? ', есть событие' : ''}`
  const periodTitle =
    mode === 'day' ? formatDayLong(selected) : mode === 'week' ? formatWeekRange(startOfWeek(selected)) : formatMonth(selected)

  return (
    <Screen title="Расписание">
      <button
        type="button"
        className="pc-btn pc-btn--block pc-btn--row"
        style={{ marginTop: 16, minHeight: 56 }}
        onClick={() => navigate('/schedule/events')}
      >
        <span style={{ minWidth: 0 }}>
          <span style={{ display: 'block' }}>Соревнования и сборы</span>
          <span className="pc-list-row__meta" style={{ display: 'block', fontWeight: 400 }}>
            {events.isError
              ? 'Не загрузились, откройте раздел'
              : nextEvent
                ? `Ближайшее: ${nextEvent.name}, ${formatEventWhen(nextEvent, today.getFullYear(), false)}`
                : events.data
                  ? 'Ближайших нет'
                  : ' '}
          </span>
        </span>
        <ChevronRight size={20} aria-hidden="true" />
      </button>

      <div className="pc-segments" role="group" aria-label="Период" style={{ marginTop: 12 }}>
        {MODES.map((m) => (
          <button key={m.id} type="button" aria-pressed={mode === m.id} onClick={() => update({ m: m.id })}>
            {m.label}
          </button>
        ))}
      </div>

      <div className="pc-week-nav">
        <button type="button" className="pc-icon-btn" onClick={() => step(-1)} aria-label="Назад по календарю">
          <ChevronLeft size={22} aria-hidden="true" />
        </button>
        <h2 aria-live="polite">{periodTitle}</h2>
        <button type="button" className="pc-icon-btn" onClick={() => step(1)} aria-label="Вперёд по календарю">
          <ChevronRight size={22} aria-hidden="true" />
        </button>
      </div>

      {mode === 'week' && (
        <div className="pc-week" role="group" aria-label="Дни недели">
          {Array.from({ length: 7 }, (_, i) => {
            const d = addDays(startOfWeek(selected), i)
            const iso = isoDate(d)
            return (
              <button
                key={iso}
                type="button"
                aria-pressed={iso === isoDate(selected)}
                aria-label={dayLabel(d, iso)}
                onClick={() => update({ d })}
              >
                <span>{weekdayShort(d)}</span>
                <span>{d.getDate()}</span>
                <DayDots sessions={daysWithSessions.has(iso)} event={hasEvent(iso)} />
              </button>
            )
          })}
        </div>
      )}

      {mode === 'month' && (
        <div className="pc-month" role="group" aria-label="Дни месяца">
          {WEEK_HEAD.map((h) => (
            <div key={h} className="pc-month__head" aria-hidden="true">
              {h}
            </div>
          ))}
          {Array.from({ length: 42 }, (_, i) => {
            const d = addDays(from, i)
            const iso = isoDate(d)
            return (
              <button
                key={iso}
                type="button"
                aria-pressed={iso === isoDate(selected)}
                aria-label={dayLabel(d, iso)}
                data-outside={d.getMonth() !== selected.getMonth()}
                data-today={iso === isoDate(today)}
                onClick={() => update({ d })}
              >
                {d.getDate()}
                <DayDots sessions={daysWithSessions.has(iso)} event={hasEvent(iso)} />
              </button>
            )
          })}
        </div>
      )}

      <section className="pc-section" aria-labelledby="pc-day-title">
        {mode !== 'day' && (
          <h2 id="pc-day-title" className="pc-section-title">
            {formatDayLong(selected)}
          </h2>
        )}
        {dayEvents.map((e) => (
          <EventCard key={e.id} event={e} thisYear={today.getFullYear()} onOpen={() => navigate(`/schedule/events/${e.id}`)} />
        ))}
        {sessions.isPending ? (
          <Skeleton rows={2} label="Загружаю расписание" />
        ) : sessions.isError ? (
          <ErrorState title="Не удалось загрузить расписание" onRetry={() => void sessions.refetch()} />
        ) : dayList.length === 0 ? (
          <EmptyState title="На этот день занятий нет" text={formatDayLong(selected)} />
        ) : (
          dayList.map((s) => <SlotCard key={s.key} slot={s} onOpen={() => navigate(`/sessions/${encodeURIComponent(s.key)}`)} />)
        )}
      </section>

      <button
        type="button"
        className="pc-btn pc-btn--block"
        style={{ marginTop: 12 }}
        onClick={() => toast('Добавление занятия будет на следующем этапе')}
      >
        Добавить занятие
      </button>
    </Screen>
  )
}

function SlotCard({ slot, onOpen }: { slot: SessionSlot; onOpen: () => void }) {
  // Отменённое тоже открывается: на экране занятия его можно вернуть.
  return (
    <button type="button" className="pc-slot" data-cancelled={slot.cancelled || undefined} onClick={onOpen}>
      <div className="pc-slot__time">{formatTimeRange(slot)}</div>
      <div className="pc-slot__title">{slot.groupName}</div>
      {slot.cancelled && (
        <span className="pc-badge pc-badge--neutral" style={{ marginTop: 6 }}>
          Отменено
        </span>
      )}
    </button>
  )
}

/** Точка - занятия, кольцо - событие: в неделе и месяце не путаются. */
function DayDots({ sessions, event }: { sessions: boolean; event: boolean }) {
  if (!sessions && !event) return null
  return (
    <span className="pc-week__dots" aria-hidden="true">
      {sessions && <span className="pc-week__dot" />}
      {event && <span className="pc-week__dot pc-week__dot--event" />}
    </span>
  )
}

/** Событие дня над занятиями: тип, время и название, ведёт в карточку события. */
function EventCard({ event: e, thisYear, onOpen }: { event: ClubEvent; thisYear: number; onOpen: () => void }) {
  return (
    <button type="button" className="pc-slot pc-slot--event" onClick={onOpen} style={{ marginBottom: 8 }}>
      <div className="pc-slot__time">
        {EVENT_KIND_LABEL[e.kind]}
        {e.start.time ? ` / ${e.start.time}` : ''}
        {e.end && e.end.date > e.start.date ? ` / ${formatEventWhen(e, thisYear, false)}` : ''}
      </div>
      <div className="pc-slot__title">{e.name}</div>
      {e.location && <div className="pc-muted" style={{ fontSize: 14, marginTop: 2 }}>{e.location}</div>}
    </button>
  )
}
